import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// Every line the billing screens, dialogs and toasts show, in English and Spanish, read through
// `useBillingCopy` (behavior/use-billing-copy.ts). The plan cards' own lines belong to the billing
// contract and read through `plan-lines-copy.ts`; error copy stays in `billing-error-copy.ts`.

export type BillingCatalog = typeof EN;

const EN = {
  // Screens and navigation.
  titlePricing: "Plans · {product}",
  titleSubscription: "Subscription · {product}",
  titleCredits: "Credits · {product}",
  titleInvoices: "Invoices · {product}",
  titleBillingDetails: "Billing details · {product}",
  navSubscription: "Subscription",
  navPlans: "Plans",
  navCredits: "Credits",
  navInvoices: "Invoices",
  navBillingDetails: "Billing details",
  crumbHome: "Home",
  crumbSettings: "Settings",
  plansTitle: "Plans",
  plansDescription: "Compare the plans and choose the one that fits your team.",
  guarantee14Day: "14-day money-back guarantee",
  guarantee24Hour: "24-hour support",
  guaranteeCancel: "Cancel anytime",
  subscriptionTitle: "Subscription",
  subscriptionDescription: "Your current plan, its status and when it renews.",
  invoicesTitle: "Invoices",
  invoicesDescription: "Every order you paid for, with its invoice to download.",
  openingCreditPacks: "Opening credit packs…",

  // Words shared by several screens.
  cancel: "Cancel",
  close: "Close",
  continueButton: "Continue",
  used: "Used",
  unlimited: "Unlimited",
  statusLabel: "Status",
  buyCredits: "Buy credits",
  buyAiCredits: "Buy AI credits",
  credits: "Credits",

  // The subscription panel.
  currentPlanTitle: "Current plan",
  noSubscriptionTitle: "You don't have any active subscription",
  noSubscriptionDescription: "To use every feature, you need an active subscription.",
  subscribeToPlan: "Subscribe to a plan",
  yourPlan: "Your plan",
  planBilledMonthly: "{plan} · billed monthly",
  planBilledAnnually: "{plan} · billed annually",
  statusNone: "No subscription",
  statusActive: "Active",
  statusPaymentFailed: "Payment failed",
  accessUntil: "Access until",
  statusCancelling: "Cancelling",
  statusFrozen: "Frozen",
  price: "Price",
  pricePerMonth: "{amount} / month",
  pricePerYear: "{amount} / year",
  discount: "Discount",
  discountUntil: "{percent}% until {date}",
  freeUntil: "Free until",
  cancelsAt: "Cancels at",
  frozenUntil: "Frozen until",
  renews: "Renews",
  changePlan: "Change plan",
  managePaymentMethod: "Manage payment method",
  cancelSubscription: "Cancel subscription",
  doNotCancelSubscription: "Do not cancel the subscription",
  deactivateFreeze: "Deactivate freeze",
  orderHistory: "Order History",
  columnDate: "Date",
  columnDescription: "Description",
  columnAmount: "Amount",
  columnInvoice: "Invoice",
  download: "Download",
  noOrdersYet: "No orders yet",
  invoicePaid: "PAID",
  invoiceRefunded: "REFUNDED",
  reactivateTitle: "Reactivate subscription?",
  reactivateDescription:
    "Your subscription will remain active and will renew automatically. You won't lose access to anything.",
  reactivate: "Reactivate",
  subscriptionNoLongerCancelled: "Your subscription will no longer be cancelled.",
  couldNotReactivate: "Could not reactivate your subscription",
  deactivateFreezeDescription:
    "Are you sure you want to deactivate the freeze? You will lose the reduced price benefit and go back to your regular price.",
  subscriptionUnfrozen: "Your subscription has been unfrozen.",
  couldNotDeactivateFreeze: "Could not deactivate the freeze",
  couldNotOpenPortal: "Could not open the billing portal",
  checkoutSucceededTitle: "Payment received",
  checkoutSucceededDescription: "Your plan and credits update in a moment.",
  checkoutCancelledTitle: "Checkout cancelled",
  checkoutCancelledDescription: "Nothing was charged.",
  couldNotDownloadInvoice: "Could not download this invoice",

  // Billing details.
  billingDetailsTitle: "Billing details",
  billingDetailsDescription: "Shown on every invoice document you download.",
  fieldName: "Name",
  fieldBillingEmail: "Billing email",
  fieldAddress: "Address",
  fieldTaxId: "Tax id",
  saveBillingDetails: "Save billing details",
  billingDetailsSaved: "Billing details saved.",
  couldNotSaveBillingDetails: "Could not save billing details",

  // Seats and auto-recharge.
  seatsTitle: "Seats",
  seatsIncluded: "Included in your plan",
  seatsExtra: "Extra seats bought",
  seatsBilled: "Billed per period",
  seatsBilledValue: "{count} × {price}",
  seatsNoneBilled: "None, within your plan",
  addSeat: "Add a seat",
  seatsLeft: {
    one: "1 seat is still free. For more than {max}, upgrade your plan.",
    other: "{count} seats are still free. For more than {max}, upgrade your plan.",
  },
  seatsNoLimit: "Your plan has no seat limit: invite as many teammates as you like.",
  seatsAdded: { one: "Added 1 seat.", other: "Added {count} seats." },
  couldNotAddSeats: "Could not add seats",
  autoRechargeTitle: "Auto-recharge",
  autoRechargeSwitch: "Buy more credits automatically",
  autoRechargeSwitchHint: "When your balance drops below a threshold, a credit pack is bought for you.",
  thresholdLabel: "Threshold (credits)",
  packToBuy: "Pack to buy",
  packOption: { one: "{count} credit ({price})", other: "{count} credits ({price})" },
  saveAutoRecharge: "Save auto-recharge settings",
  autoRechargeFailedTitle: "The last auto-recharge attempt failed",
  autoRechargeSaved: "Auto-recharge settings saved.",
  couldNotSaveAutoRecharge: "Could not save auto-recharge settings",

  // Buying credits.
  buyAdditionalCredits: "Buy additional credits",
  youPayTodayAmount: "You pay today: {amount}",
  openingCheckout: "Opening checkout...",
  continueToPayment: "Continue to payment",
  couldNotStartCheckout: "The checkout could not be started",
  creditType: "Credit type",
  analysisCreditsTab: "Analysis Credits",
  aiCreditsTab: "AI Credits",
  creditPacksHint:
    "The more you buy, the cheaper each credit gets. You pay through our payment provider's own checkout page.",
  payWith: "Pay with",
  payWithCard: "Card",
  payWithCardHint: "Through our payment provider's checkout page.",
  payWithBitcoin: "Bitcoin",
  payWithBitcoinHint: "Lightning, through Swiss Bitcoin Pay. The price is fixed for 15 minutes.",
  packTitle: { one: "{count} credit", other: "{count} credits" },
  packAiTitle: { one: "{count} AI credit", other: "{count} AI credits" },
  packPrice: "{price} · {unit} per 1,000",
  packRecommended: "Recommended",
  packSave: "Save {percent}%",

  // Changing plan.
  upgradeTo: "Upgrade to {plan}",
  reviewPlan: "Review your plan before paying.",
  continueToPaymentAmount: "Continue to payment, {amount}",
  fieldPlan: "Plan",
  fieldBilling: "Billing",
  periodMonthly: "Monthly",
  periodYearly: "Yearly",
  periodYearlyDiscount: "Yearly ({percent}% off)",
  youPayTodayLabel: "You pay today",
  providerPageTitle: "You pay on the provider's own page",
  providerPageDescription:
    "The plan switches on as soon as it confirms the charge; nothing real is ever charged.",

  // The plan cards, the comparison and the questions.
  billingPeriod: "Billing period",
  perMonthSuffix: "/month",
  billedAnnually: "{total} billed annually · You save {amount} a month",
  billedMonthly: "Billed monthly",
  upgradeNow: "Upgrade Now",
  comparePlansTitle: "Compare Plans",
  comparePlansDescription: "See which plan is right for your business",
  comparePlansLabel: "Compare plans",
  featureHeader: "Feature",
  included: "Included",
  faqTitle: "Frequently asked questions",
  faqDescription: "We are here to help!",
  faqCreditQuestion: "What is a credit?",
  faqCreditAnswer:
    "A credit is a unit of measurement within the tool. Each profile an analysis examines that has data costs credits, whether or not an email is found. You can buy more credits once you have an active plan.",
  faqEmailsQuestion: "How many emails can I get with 3,000 credits?",
  faqEmailsAnswer:
    "It depends on the audience: business-heavy audiences publish more contact details. Analyses show their email yield as they run.",
  faqLegalQuestion: "Is it legal to send cold emails?",
  faqLegalAnswer:
    "B2B cold outreach is legal in most countries when you identify yourself, keep it relevant and honour unsubscribes. Every campaign includes an unsubscribe link.",
  faqTrainingQuestion: "Will I know how to use it?",
  faqTrainingAnswer:
    "Yes: every plan includes video training and 24-hour support, and the dashboard walks you through your first analysis.",
  faqAccessQuestion: "Once I have made the purchase, how do I access the software?",
  faqAccessAnswer: "Right away: the plan applies to your account the moment the payment is confirmed.",
  faqCountryQuestion: "Will it work in my country?",
  faqCountryAnswer: "Yes. Analyses work on public profiles from any country.",

  // The credit history.
  creditsDescription: "One balance for the whole organization: buy more, or let it recharge on its own.",
  creditsDescriptionBuyOnly: "One balance for the whole organization: buy more when you need it.",
  creditsPanelDescription: "One balance, shared by every member",
  planCreditsUsed: "Plan credits this period",
  creditsUnlimited: "Unlimited on your plan.",
  offSalePlanTitle: "You are on {plan}",
  offSalePlanDescription:
    "This plan is no longer on sale. It stays yours, with the same limits and the same price.",
  switchToPlan: "Switch to {plan}",
  creditsLeft: "{left} left",
  creditsCarryOver: "{carried} of them never expire.",
  welcomeWithheldTitle: "No free credits on this account",
  welcomeWithheldDescription:
    "Free credits are given once per person, and this account didn't receive them. Buy a credit pack or choose a plan to keep going.",
  aiCreditsTitle: "AI credits",
  aiCreditsDescription: "Bought in packs, spent by enrichment and our model",
  aiCreditsLeft: "{left} left.",
  historyTitle: "History",
  historyDescription: "The latest 100 credits spent or added, newest first, with the member who caused it.",
  creditHistoryLabel: "Credit history",
  noCreditsSpentTitle: "No credits spent yet",
  noCreditsSpentDescription: "Run an analysis to see it here.",
  columnActivity: "Activity",
  columnMember: "Member",
  columnType: "Type",
  movementAnalysis: "Analysis",
  movementEnrichment: "Enrichment",
  movementPurchase: "Credit pack",
  movementTask: "Reward for a task",
  movementMilestone: "Milestone reward",
  movementWelcome: "Welcome credits",
  movementReferral: "Referral reward",
  movementAdjustment: "Adjustment",
  movementRenewal: "Monthly credits",
  movementAutoRecharge: "Auto-recharge",
  movementRemoteAgent: "Remote agent",
  movementAgentModel: "Remote agent on our model",
  movementAgentModelNamed: "Agent model · {agent}",
} as const;

const ES: Translation<BillingCatalog> = {
  titlePricing: "Planes · {product}",
  titleSubscription: "Suscripción · {product}",
  titleCredits: "Créditos · {product}",
  titleInvoices: "Facturas · {product}",
  titleBillingDetails: "Datos de facturación · {product}",
  navSubscription: "Suscripción",
  navPlans: "Planes",
  navCredits: "Créditos",
  navInvoices: "Facturas",
  navBillingDetails: "Datos de facturación",
  crumbHome: "Inicio",
  crumbSettings: "Ajustes",
  plansTitle: "Planes",
  plansDescription: "Compara los planes y elige el que encaja con tu equipo.",
  guarantee14Day: "Garantía de devolución de 14 días",
  guarantee24Hour: "Soporte 24 horas",
  guaranteeCancel: "Cancela cuando quieras",
  subscriptionTitle: "Suscripción",
  subscriptionDescription: "Tu plan actual, su estado y cuándo se renueva.",
  invoicesTitle: "Facturas",
  invoicesDescription: "Cada pedido que pagaste, con su factura para descargar.",
  openingCreditPacks: "Abriendo los paquetes de créditos…",

  cancel: "Cancelar",
  close: "Cerrar",
  continueButton: "Continuar",
  used: "Usados",
  unlimited: "Ilimitado",
  statusLabel: "Estado",
  buyCredits: "Comprar créditos",
  buyAiCredits: "Comprar créditos de IA",
  credits: "Créditos",

  currentPlanTitle: "Plan actual",
  noSubscriptionTitle: "No tienes ninguna suscripción activa",
  noSubscriptionDescription: "Para usar todas las funciones, necesitas una suscripción activa.",
  subscribeToPlan: "Suscribirme a un plan",
  yourPlan: "Tu plan",
  planBilledMonthly: "{plan} · facturación mensual",
  planBilledAnnually: "{plan} · facturación anual",
  statusNone: "Sin suscripción",
  statusActive: "Activa",
  statusPaymentFailed: "Pago fallido",
  accessUntil: "Acceso hasta",
  statusCancelling: "En cancelación",
  statusFrozen: "Congelada",
  price: "Precio",
  pricePerMonth: "{amount} / mes",
  pricePerYear: "{amount} / año",
  discount: "Descuento",
  discountUntil: "{percent}% hasta el {date}",
  freeUntil: "Gratis hasta",
  cancelsAt: "Se cancela el",
  frozenUntil: "Congelada hasta",
  renews: "Se renueva",
  changePlan: "Cambiar de plan",
  managePaymentMethod: "Administrar forma de pago",
  cancelSubscription: "Cancelar suscripción",
  doNotCancelSubscription: "No cancelar la suscripción",
  deactivateFreeze: "Desactivar congelación",
  orderHistory: "Historial de pedidos",
  columnDate: "Fecha",
  columnDescription: "Descripción",
  columnAmount: "Importe",
  columnInvoice: "Factura",
  download: "Descargar",
  noOrdersYet: "Aún no hay pedidos",
  invoicePaid: "PAGADA",
  invoiceRefunded: "REEMBOLSADA",
  reactivateTitle: "¿Reactivar la suscripción?",
  reactivateDescription:
    "Tu suscripción seguirá activa y se renovará automáticamente. No perderás el acceso a nada.",
  reactivate: "Reactivar",
  subscriptionNoLongerCancelled: "Tu suscripción ya no se cancelará.",
  couldNotReactivate: "No pudimos reactivar tu suscripción",
  deactivateFreezeDescription:
    "¿Seguro que quieres desactivar la congelación? Perderás el precio reducido y volverás a tu precio habitual.",
  subscriptionUnfrozen: "Tu suscripción se ha descongelado.",
  couldNotDeactivateFreeze: "No pudimos desactivar la congelación",
  couldNotOpenPortal: "No pudimos abrir el portal de facturación",
  checkoutSucceededTitle: "Pago recibido",
  checkoutSucceededDescription: "Tu plan y tus créditos se actualizan en un momento.",
  checkoutCancelledTitle: "Pago cancelado",
  checkoutCancelledDescription: "No se cobró nada.",
  couldNotDownloadInvoice: "No pudimos descargar esta factura",

  billingDetailsTitle: "Datos de facturación",
  billingDetailsDescription: "Se muestran en cada factura que descargues.",
  fieldName: "Nombre",
  fieldBillingEmail: "Correo de facturación",
  fieldAddress: "Dirección",
  fieldTaxId: "Identificación fiscal",
  saveBillingDetails: "Guardar datos de facturación",
  billingDetailsSaved: "Datos de facturación guardados.",
  couldNotSaveBillingDetails: "No pudimos guardar los datos de facturación",

  seatsTitle: "Puestos",
  seatsIncluded: "Incluidos en tu plan",
  seatsExtra: "Puestos extra comprados",
  seatsBilled: "Facturados por periodo",
  seatsBilledValue: "{count} × {price}",
  seatsNoneBilled: "Ninguno, dentro de tu plan",
  addSeat: "Agregar un puesto",
  seatsLeft: {
    one: "Queda 1 puesto libre. Para más de {max}, mejora tu plan.",
    other: "Quedan {count} puestos libres. Para más de {max}, mejora tu plan.",
  },
  seatsNoLimit: "Tu plan no tiene límite de puestos: invita a tantas personas como quieras.",
  seatsAdded: { one: "Se agregó 1 puesto.", other: "Se agregaron {count} puestos." },
  couldNotAddSeats: "No pudimos agregar puestos",
  autoRechargeTitle: "Recarga automática",
  autoRechargeSwitch: "Comprar más créditos automáticamente",
  autoRechargeSwitchHint: "Cuando tu saldo baje de un umbral, compramos un paquete de créditos por ti.",
  thresholdLabel: "Umbral (créditos)",
  packToBuy: "Paquete a comprar",
  packOption: { one: "{count} crédito ({price})", other: "{count} créditos ({price})" },
  saveAutoRecharge: "Guardar ajustes de recarga automática",
  autoRechargeFailedTitle: "Falló el último intento de recarga automática",
  autoRechargeSaved: "Ajustes de recarga automática guardados.",
  couldNotSaveAutoRecharge: "No pudimos guardar los ajustes de recarga automática",

  buyAdditionalCredits: "Comprar créditos adicionales",
  youPayTodayAmount: "Pagas hoy: {amount}",
  openingCheckout: "Abriendo el pago...",
  continueToPayment: "Continuar al pago",
  couldNotStartCheckout: "No pudimos iniciar el pago",
  creditType: "Tipo de crédito",
  analysisCreditsTab: "Créditos de análisis",
  aiCreditsTab: "Créditos de IA",
  creditPacksHint:
    "Cuanto más compras, más barato sale cada crédito. Pagas en la página de pago de nuestro proveedor.",
  payWith: "Pagar con",
  payWithCard: "Tarjeta",
  payWithCardHint: "En la página de pago de nuestro proveedor.",
  payWithBitcoin: "Bitcoin",
  payWithBitcoinHint: "Lightning, con Swiss Bitcoin Pay. El precio se mantiene 15 minutos.",
  packTitle: { one: "{count} crédito", other: "{count} créditos" },
  packAiTitle: { one: "{count} crédito de IA", other: "{count} créditos de IA" },
  packPrice: "{price} · {unit} por cada 1.000",
  packRecommended: "Recomendado",
  packSave: "Ahorra {percent}%",

  upgradeTo: "Mejorar a {plan}",
  reviewPlan: "Revisa tu plan antes de pagar.",
  continueToPaymentAmount: "Continuar al pago, {amount}",
  fieldPlan: "Plan",
  fieldBilling: "Facturación",
  periodMonthly: "Mensual",
  periodYearly: "Anual",
  periodYearlyDiscount: "Anual ({percent}% de descuento)",
  youPayTodayLabel: "Pagas hoy",
  providerPageTitle: "Pagas en la página de nuestro proveedor",
  providerPageDescription:
    "El plan se activa en cuanto el proveedor confirma el cobro. Nunca se cobra nada real.",

  billingPeriod: "Periodo de facturación",
  perMonthSuffix: "/mes",
  billedAnnually: "{total} al año · Ahorras {amount} al mes",
  billedMonthly: "Facturación mensual",
  upgradeNow: "Mejorar ahora",
  comparePlansTitle: "Compara los planes",
  comparePlansDescription: "Mira qué plan es el mejor para tu negocio",
  comparePlansLabel: "Comparar planes",
  featureHeader: "Función",
  included: "Incluido",
  faqTitle: "Preguntas frecuentes",
  faqDescription: "¡Estamos aquí para ayudarte!",
  faqCreditQuestion: "¿Qué es un crédito?",
  faqCreditAnswer:
    "Un crédito es una unidad de medida dentro de la herramienta. Cada perfil con datos que revisa un análisis cuesta créditos, se encuentre o no un correo. Puedes comprar más créditos cuando tengas un plan activo.",
  faqEmailsQuestion: "¿Cuántos correos puedo obtener con 3.000 créditos?",
  faqEmailsAnswer:
    "Depende de la audiencia: las audiencias de empresas publican más datos de contacto. Los análisis muestran cuántos correos encuentran mientras se ejecutan.",
  faqLegalQuestion: "¿Es legal enviar correos en frío?",
  faqLegalAnswer:
    "El contacto en frío entre empresas es legal en la mayoría de los países si te identificas, eres relevante y respetas las bajas. Cada campaña incluye un enlace para darse de baja.",
  faqTrainingQuestion: "¿Sabré cómo usarlo?",
  faqTrainingAnswer:
    "Sí: todos los planes incluyen capacitación en video y soporte 24 horas, y el panel te guía en tu primer análisis.",
  faqAccessQuestion: "Una vez hecha la compra, ¿cómo accedo al software?",
  faqAccessAnswer: "De inmediato: el plan se aplica a tu cuenta en cuanto se confirma el pago.",
  faqCountryQuestion: "¿Funcionará en mi país?",
  faqCountryAnswer: "Sí. Los análisis funcionan con perfiles públicos de cualquier país.",

  creditsDescription: "Un saldo para toda la organización: compra más o deja que se recargue solo.",
  creditsDescriptionBuyOnly: "Un saldo para toda la organización: compra más cuando lo necesites.",
  creditsPanelDescription: "Un solo saldo, compartido por todos los miembros",
  planCreditsUsed: "Créditos del plan este periodo",
  creditsUnlimited: "Ilimitados en tu plan.",
  offSalePlanTitle: "Estás en {plan}",
  offSalePlanDescription:
    "Este plan ya no está a la venta. Sigue siendo tuyo, con los mismos límites y el mismo precio.",
  switchToPlan: "Cambiar a {plan}",
  creditsLeft: "Quedan {left}",
  creditsCarryOver: "{carried} de ellos nunca vencen.",
  welcomeWithheldTitle: "Esta cuenta no tiene créditos gratis",
  welcomeWithheldDescription:
    "Los créditos gratis se entregan una vez por persona, y esta cuenta no los recibió. Compra un paquete de créditos o elige un plan para seguir adelante.",
  aiCreditsTitle: "Créditos de IA",
  aiCreditsDescription: "Se compran en paquetes y los gastan el enriquecimiento y nuestro modelo",
  aiCreditsLeft: "Quedan {left}.",
  historyTitle: "Historial",
  historyDescription:
    "Los últimos 100 créditos gastados o agregados, del más reciente al más antiguo, con el miembro que los usó.",
  creditHistoryLabel: "Historial de créditos",
  noCreditsSpentTitle: "Aún no has gastado créditos",
  noCreditsSpentDescription: "Ejecuta un análisis para verlo aquí.",
  columnActivity: "Actividad",
  columnMember: "Miembro",
  columnType: "Tipo",
  movementAnalysis: "Análisis",
  movementEnrichment: "Enriquecimiento",
  movementPurchase: "Paquete de créditos",
  movementTask: "Recompensa por una tarea",
  movementMilestone: "Recompensa por un hito",
  movementWelcome: "Créditos de bienvenida",
  movementReferral: "Recompensa por referido",
  movementAdjustment: "Ajuste",
  movementRenewal: "Créditos del mes",
  movementAutoRecharge: "Recarga automática",
  movementRemoteAgent: "Agente remoto",
  movementAgentModel: "Agente remoto con nuestro modelo",
  movementAgentModelNamed: "Modelo de agente · {agent}",
};

export const BILLING_CATALOGS: Catalogs<BillingCatalog> = { en: EN, es: ES };
